import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Check, Loader2, Pause, Play, X } from 'lucide-react';
import { supabase } from '../lib/supabase';

export interface Voice {
  id: string;
  nombre: string;
  sexo: string;
  descripcion: string;
  preview_url: string;
  idioma: string;
  acento: string;
}

interface VoiceSelectorProps {
  open: boolean;
  selectedVoiceId: string | null;
  onSelect: (voiceId: string) => void;
  onClose: () => void;
}

const VoiceSelector: React.FC<VoiceSelectorProps> = ({ open, selectedVoiceId, onSelect, onClose }) => {
  const [voices, setVoices] = useState<Voice[]>([]);
  const [loading, setLoading] = useState(true);
  const [animated, setAnimated] = useState(false);
  const [dragY, setDragY] = useState(0);
  const dragStartY = useRef<number | null>(null);
  const [playingId, setPlayingId] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [sexFilter, setSexFilter] = useState<string>('Todas');

  // Fetch voices
  useEffect(() => {
    let cancelled = false;
    const fetchVoices = async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from('voces')
        .select('id, nombre, sexo, descripcion, preview_url, idioma, acento')
        .order('nombre');
      if (!cancelled) {
        if (!error && data) setVoices(data as Voice[]);
        setLoading(false);
      }
    };
    fetchVoices();
    return () => { cancelled = true; };
  }, []);

  // Animate in
  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => requestAnimationFrame(() => setAnimated(true)));
    }
  }, [open]);

  // Stop audio when closing
  const handleClose = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setPlayingId(null);
    setAnimated(false);
    setTimeout(() => onClose(), 300);
  }, [onClose]);

  // Touch handlers for mobile drag-to-dismiss
  const handleTouchStart = (e: React.TouchEvent) => {
    dragStartY.current = e.touches[0].clientY;
  };
  const handleTouchMove = (e: React.TouchEvent) => {
    if (dragStartY.current === null) return;
    e.preventDefault();
    const delta = e.touches[0].clientY - dragStartY.current;
    if (delta > 0) setDragY(delta);
  };
  const handleTouchEnd = () => {
    if (dragY > 100) handleClose();
    setDragY(0);
    dragStartY.current = null;
  };

  // Audio preview
  const togglePreview = useCallback((voice: Voice) => {
    if (playingId === voice.id) {
      // Stop
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      setPlayingId(null);
      return;
    }
    // Stop previous
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    if (!voice.preview_url) return;
    const audio = new Audio(voice.preview_url);
    audioRef.current = audio;
    setPlayingId(voice.id);
    audio.play().catch(() => setPlayingId(null));
    audio.addEventListener('ended', () => {
      setPlayingId(null);
      audioRef.current = null;
    });
  }, [playingId]);

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  const sexOptions = useMemo(() => {
    const set = new Set(voices.map(v => v.sexo).filter(Boolean));
    return ['Todas', ...Array.from(set).sort()];
  }, [voices]);

  const filteredVoices = useMemo(() => {
    if (sexFilter === 'Todas') return voices;
    return voices.filter(v => v.sexo === sexFilter);
  }, [voices, sexFilter]);

  // Clean up messy descriptions (some have internal reasoning text)
  const cleanDescription = (desc: string) => {
    if (!desc) return '';
    // If description contains newlines with "SILENT THOUGHT" or similar, take only the first meaningful line
    if (desc.includes('SILENT THOUGHT') || desc.includes('\\n')) return '';
    // Truncate overly long descriptions
    if (desc.length > 100) return desc.substring(0, 100) + '…';
    return desc;
  };

  if (!open) return null;

  const voiceCard = (voice: Voice) => {
    const isSelected = selectedVoiceId === voice.id;
    const isPlaying = playingId === voice.id;
    const desc = cleanDescription(voice.descripcion);
    // Extract short name (before " - " if present)
    const shortName = (voice.nombre || '').includes(' - ') ? voice.nombre.split(' - ')[0] : (voice.nombre || 'Sin nombre');

    return (
      <button
        key={voice.id}
        type="button"
        onClick={() => onSelect(voice.id)}
        className={`w-full text-left rounded-[13px] p-4 transition-all ${
          isSelected
            ? 'bg-[#1644EB]/[0.06] ring-2 ring-[#1644EB]'
            : 'bg-white ring-1 ring-black/[0.06] active:bg-black/[0.02]'
        }`}
      >
        <div className="flex items-center gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-[15px] font-medium text-brand-black/90 truncate">{shortName}</span>
              {isSelected && <Check className="w-4 h-4 text-[#1644EB] shrink-0" />}
            </div>
            {(voice.idioma || voice.acento) && (
              <p className="text-[13px] text-[#3C3C43]/40 mt-0.5 flex items-center gap-1.5">
                {voice.idioma && <span>{voice.idioma}</span>}
                {voice.idioma && voice.acento && <span className="text-[#3C3C43]/25">·</span>}
                {voice.acento && <span>{voice.acento}</span>}
              </p>
            )}
            {(voice.sexo || desc) && (
              <p className="text-[13px] text-[#3C3C43]/60 mt-1 leading-relaxed">
                {voice.sexo && <span className="font-medium">{voice.sexo}</span>}
                {voice.sexo && desc && <span> · </span>}
                {desc}
              </p>
            )}
          </div>
          {voice.preview_url && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); togglePreview(voice); }}
              className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 transition-colors ${
                isPlaying
                  ? 'bg-[#1644EB] text-white'
                  : 'bg-black/[0.05] text-brand-black/50 hover:bg-black/[0.08]'
              }`}
            >
              {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
            </button>
          )}
        </div>
      </button>
    );
  };

  const filterTabs = (
    <div className="flex gap-2 overflow-x-auto no-scrollbar">
      {sexOptions.map((opt) => (
        <button
          key={opt}
          type="button"
          onClick={() => setSexFilter(opt)}
          className={`shrink-0 px-3 py-1.5 rounded-full text-[13px] font-medium transition-colors ${
            sexFilter === opt
              ? 'bg-[#1644EB] text-white'
              : 'bg-black/[0.05] text-brand-black/60 hover:bg-black/[0.08]'
          }`}
        >
          {opt}
        </button>
      ))}
    </div>
  );

  const voiceList = loading ? (
    <div className="flex items-center justify-center py-12">
      <Loader2 className="w-6 h-6 text-[#1644EB] animate-spin" />
    </div>
  ) : filteredVoices.length === 0 ? (
    <div className="flex items-center justify-center py-12">
      <p className="text-[15px] text-[#3C3C43]/60">No se encontraron voces.</p>
    </div>
  ) : (
    <div className="flex flex-col gap-3">
      {filteredVoices.map(voiceCard)}
    </div>
  );

  return (
    <>
      {/* Desktop Side Panel */}
      <div
        className={`hidden sm:flex fixed top-4 right-4 bottom-4 w-[630px] bg-white rounded-[13px] shadow-[0_8px_40px_rgba(0,0,0,0.12),0_0_0_0.5px_rgba(0,0,0,0.06)] z-[50] flex-col overflow-hidden transition-transform duration-300 ease-out ${
          animated ? 'translate-x-0' : 'translate-x-[calc(100%+32px)]'
        }`}
      >
        <div className="flex items-center justify-between px-5 py-4">
          <h3 className="text-[17px] font-semibold text-brand-black/90">Seleccionar voz</h3>
          <button type="button" onClick={handleClose} className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-brand-black/40 hover:bg-black/[0.1] transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="mx-5 h-px bg-black/[0.08]" />
        <div className="px-5 pt-4 pb-2">
          {filterTabs}
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-3">
          {voiceList}
        </div>
        <div className="px-5 py-3 flex justify-end">
          <button type="button" onClick={handleClose} className="text-[15px] font-semibold rounded-[10px] px-4 py-2 shadow-sm transition-colors text-white bg-[#1644EB] hover:bg-[#1238C8]">Aceptar</button>
        </div>
      </div>

      {/* Mobile Bottom Sheet via portal */}
      {ReactDOM.createPortal(
        <>
          <div
            className={`sm:hidden fixed inset-0 z-[200] bg-black/40 transition-all duration-300 ${animated ? 'opacity-100' : 'opacity-0'}`}
            onClick={handleClose}
          />
          <div
            className="sm:hidden fixed inset-x-0 bottom-0 z-[201] bg-[#F2F2F7] rounded-t-[13px] flex flex-col"
            style={{
              height: 'calc(100dvh - 1rem)',
              paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)',
              transform: dragY > 0
                ? `translateY(${dragY}px)`
                : animated ? 'translateY(0)' : 'translateY(100%)',
              transition: dragY === 0 ? 'transform 0.3s ease-out' : 'none',
            }}
          >
            <div
              className="py-2 cursor-grab active:cursor-grabbing"
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              <div className="w-9 h-[5px] bg-black/[0.15] rounded-full mx-auto" />
            </div>
            <div className="flex items-center justify-between px-4 pb-3">
              <button type="button" onClick={handleClose} className="text-[15px] text-[#1644EB] active:opacity-50 transition-opacity w-20 text-left">Cancelar</button>
              <h3 className="text-[15px] font-semibold text-brand-black/90">Voz</h3>
              <button type="button" onClick={handleClose} className="text-[15px] font-semibold text-[#1644EB] active:opacity-50 transition-opacity w-20 text-right">Aceptar</button>
            </div>
            <div className="px-4 pb-2">
              {filterTabs}
            </div>
            <div className="flex-1 overflow-y-auto px-4 py-3">
              {voiceList}
            </div>
          </div>
        </>,
        document.body,
      )}
    </>
  );
};

export default VoiceSelector;
